import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { ArrowBackIosOutlined } from '@mui/icons-material';
import {
  Box,
  Typography,
  styled,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  AppBar,
  FormControlLabel,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import type {
  TripLegInvoiceItem,
  TripLegID,
} from '../../../data/models/trip-leg.model';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { ScrollableContent } from '../../ScrollableContent';
import { ProgressDialog } from '../../dialogs/ProgressDialog';
import {
  generateInvoiceAsync,
  selectSubmittedInvoice,
  selectSubmittingInvoice,
  setSubmittedInvoice,
  setSubmittingInvoice,
} from '../../../data/provider.slice';
import DialogTransition from '../DialogTransition';

const PREFIX = 'AcceptTermsDialog';

const classes = {
  appBar: `${PREFIX}-appBar`,
  container: `${PREFIX}-container`,
  mainContent: `${PREFIX}-mainContent`,
  title: `${PREFIX}-title`,
  subTitle: `${PREFIX}-subTitle`,
  bodyTitle: `${PREFIX}-bodyTitle`,
  bodyText: `${PREFIX}-bodyText`,
  bodyTextContainer: `${PREFIX}-bodyTextContainer`,
  closeButton: `${PREFIX}-closeButton`,
  previousLink: `${PREFIX}-previousLink`,
  bottomButtonGroup: `${PREFIX}-bottomButtonGroup`,
  bottomNextButton: `${PREFIX}-bottomNextButton`,
  bottomCancelButton: `${PREFIX}-bottomCancelButton`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    background:
      'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',
    padding: '48px 0px 24px 0px',
    position: 'relative',
    boxShadow: 'none',
  },
  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: '8px',
    top: '8px',
  },
  [`& .${classes.container}`]: {
    width: '100%',
    maxWidth: 1152,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    gap: '24px',
  },
  [`& .${classes.title}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  [`& .${classes.subTitle}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Bitter',
    fontSize: '36px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '48px',
    letterSpacing: '0.5px',
    padding: '0 0 24px 0',
  },
  [`& .${classes.mainContent}`]: {
    flexGrow: 1,
    overflow: 'auto',
  },
  [`& .${classes.previousLink}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  [`& .${classes.bodyTitle}`]: {
    fontFamily: 'Bitter',
    fontSize: 30,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '40px',
    letterSpacing: '0.25px',
  },
  [`& .${classes.bodyText}`]: {
    color: '#000',
    fontFamily: 'Source Sans Pro',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
    padding: '8px 24px',
    maxWidth: 846,
    width: '100%',
  },
  [`& .${classes.bodyTextContainer}`]: {
    maxHeight: 335,
    overflow: 'auto',
  },
  [`& .${classes.bottomButtonGroup}`]: {
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.bottomNextButton}`]: {
    display: 'flex',
    width: '179px',
    height: '48px',
    minWidth: '179px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [`& .${classes.bottomCancelButton}`]: {
    display: 'flex',
    width: '144px',
    height: '48px',
    minWidth: '144px',
    marginLeft: '16px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

interface AcceptTermsDialogProps {
  open: boolean;
  onClose: () => void;
  tripLegs: TripLegInvoiceItem[];
  onGenerateInvoice: () => void;
}

export default function AcceptTermsDialog({
  tripLegs,
  open,
  onClose,
  onGenerateInvoice,
}: AcceptTermsDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const providerId = useLoggedInProviderId();

  const [isAgreedToTermsChecked, setIsAgreedToTermsChecked] = useState(false);
  const [isAgreedToTermsDisabled, setIsAgreedToTermsDisabled] = useState(true);

  const submittingInvoice = useAppSelector(selectSubmittingInvoice);
  const submittedInvoice = useAppSelector(selectSubmittedInvoice);

  const handleCheckboxChange = () => {
    setIsAgreedToTermsChecked(!isAgreedToTermsChecked);
  };

  const handleTermsScrollComplete = () => {
    setIsAgreedToTermsDisabled(false);
  };

  const handleClose = () => {
    setIsAgreedToTermsDisabled(true);
    setIsAgreedToTermsChecked(false);
    onClose();
    dispatch(setSubmittingInvoice(false));
    dispatch(setSubmittedInvoice(false));
  };

  const tripLegIDs: TripLegID[] = tripLegs.map((tripLeg) => ({
    tripLegID: tripLeg.tripLegID,
  }));

  const handleSubmit = () => {
    if (isAgreedToTermsChecked && providerId) {
      dispatch(generateInvoiceAsync(providerId, tripLegIDs));
    }
  };

  useEffect(() => {
    if (submittedInvoice) {
      handleClose();
      dispatch(setSubmittingInvoice(false));
      dispatch(setSubmittedInvoice(false));
      onGenerateInvoice();
    }
  }, [submittedInvoice]);

  return (
    <StyledDialog
      fullScreen={true}
      open={open}
      onClose={onClose}
      TransitionComponent={DialogTransition}>
      {Boolean(submittingInvoice) && <LinearProgress />}
      <AppBar className={classes.appBar}>
        <IconButton
          className={classes.closeButton}
          color="default"
          onClick={onClose}
          aria-label="close"
          data-testid="close"
          size="large">
          <CloseIcon />
        </IconButton>
        <Box className={classes.container}>
          <Box>
            <Typography className={classes.title}>
              {t('generateInvoice.title')}
            </Typography>
          </Box>
        </Box>
      </AppBar>
      <DialogContent className={classes.mainContent}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Box className={classes.container}>
            <Button
              variant="text"
              startIcon={
                <ArrowBackIosOutlined
                  sx={{ width: 14, height: 14, marginRight: 0 }}
                />
              }
              onClick={handleClose}
              className={classes.previousLink}>
              {t('generateInvoice.previous')}
            </Button>
            <Typography className={classes.bodyTitle}>
              {t('generateInvoice.tripConfirmationAndComplianceAttestation')}
            </Typography>
            <ScrollableContent handleScrollComplete={handleTermsScrollComplete}>
              <Box className={classes.bodyText}>
                <ReactMarkdown>
                  {t('generateInvoice.tripConfirmationAndComplianceStatement')}
                </ReactMarkdown>
              </Box>
            </ScrollableContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAgreedToTermsChecked}
                  onChange={handleCheckboxChange}
                  disabled={isAgreedToTermsDisabled}
                />
              }
              label={t('generateInvoice.attestTripConfirmation')}
            />
            <Box className={classes.bottomButtonGroup}>
              <Button
                variant="contained"
                color="primary"
                disabled={!isAgreedToTermsChecked}
                onClick={handleSubmit}
                className={classes.bottomNextButton}>
                {t('generateInvoice.title')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleClose}
                className={classes.bottomCancelButton}>
                {t('generateInvoice.cancel')}
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      {submittingInvoice && (
        <ProgressDialog
          title={t('attestation.generatingInvoice')}
          content={t('attestation.pleaseDoNotClose')}
          open={submittingInvoice}
        />
      )}
    </StyledDialog>
  );
}
